import { createFileRoute, Outlet, useNavigate, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Menu, X, ShieldAlert } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { useAdminActivityCounts } from "@/hooks/use-admin-activity-counts";
import { useDlqToast } from "@/hooks/use-dlq-toast";
import { recordAuthEvent } from "@/lib/audit-log.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const recordEvent = useServerFn(recordAuthEvent);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (!session?.user) navigate({ to: "/login" });
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (!session?.user) navigate({ to: "/login" });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const { data: userRole } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["super_admin", "admin", "moderator"]);
      if (!data || data.length === 0) return null;
      // Returnér højeste rolle
      const priority: Record<string, number> = { super_admin: 3, admin: 2, moderator: 1 };
      return data.reduce((best, r) => (priority[r.role] > priority[best] ? r.role : best), data[0].role);
    },
    enabled: !!user,
  });

  const { data: profile } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url, full_name")
        .eq("user_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const isAdmin = userRole === "admin" || userRole === "super_admin";
  const isSuperAdmin = userRole === "super_admin";

  // MFA-status: tjek om super_admin har en verified TOTP-faktor og om sessionen er aal2
  const { data: mfaStatus } = useQuery({
    queryKey: ["mfa-status", user?.id],
    queryFn: async () => {
      const [factorsRes, aalRes] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ]);
      const hasVerifiedFactor = (factorsRes.data?.totp ?? []).some((f) => f.status === "verified");
      const currentLevel = aalRes.data?.currentLevel ?? null;
      return { hasVerifiedFactor, currentLevel };
    },
    enabled: !!user && isSuperAdmin,
    staleTime: 30 * 1000,
  });

  const {
    unreadMessages,
    urgentMessages,
    recentMessages,
    recentChatMessages,
    dlqCount,
    carsWithoutImagesCount,
    verificationStaleCount,
    verificationBadgeThresholdHours,
  } = useAdminActivityCounts({ enabled: !!user && !!userRole, isAdmin });

  // Toast when new emails land in DLQ since last sidevisning (admin-only).
  useDlqToast(dlqCount, !!user && isAdmin);

  const handleLogout = async () => {
    try {
      await recordEvent({ data: { eventType: "logout", description: "Bruger loggede ud" } });
    } catch { /* best-effort: log først, så signOut */ }
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" /></div>;
  }

  if (!user) return null;

  if (userRole === undefined) {
    return <div className="flex min-h-screen items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" /></div>;
  }

  if (userRole === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Adgang nægtet</h1>
          <p className="mt-2 text-muted-foreground">Du har ikke admin-rettigheder.</p>
          <button onClick={handleLogout} className="mt-4 text-sm text-accent hover:underline">Log ud</button>
        </div>
      </div>
    );
  }

  // MFA-håndhævelse: super_admin SKAL have aktiv MFA-faktor og være på aal2
  if (isSuperAdmin && mfaStatus !== undefined) {
    // Edge case: super_admin har factor men sessionen er aal1 (slap forbi login uden challenge)
    // → tving logout, så de skal logge ind på ny og gennemføre MFA-challenge
    if (mfaStatus.hasVerifiedFactor && mfaStatus.currentLevel !== "aal2") {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="max-w-md text-center">
            <ShieldAlert className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-4 text-2xl font-bold text-foreground">MFA-verifikation kræves</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Din session er ikke blevet MFA-verificeret. Log ud og log ind igen for at gennemføre to-faktor godkendelse.
            </p>
            <Button onClick={handleLogout} className="mt-6 bg-accent text-accent-foreground hover:brightness-110">
              Log ud og prøv igen
            </Button>
          </div>
        </div>
      );
    }

    // Super_admin uden nogen faktor → tving enrollment via /admin/profil
    if (!mfaStatus.hasVerifiedFactor) {
      const onProfilePage = typeof window !== "undefined" && window.location.pathname.startsWith("/admin/profil");
      if (!onProfilePage) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-background px-4">
            <div className="max-w-md text-center">
              <ShieldAlert className="mx-auto h-12 w-12 text-amber-500" />
              <h1 className="mt-4 text-2xl font-bold text-foreground">To-faktor godkendelse påkrævet</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Som super-admin skal du aktivere MFA før du kan tilgå admin-panelet. Det tager under et minut med en authenticator-app.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <Link to="/admin/profil">
                  <Button className="bg-accent text-accent-foreground hover:brightness-110">
                    Aktivér MFA nu
                  </Button>
                </Link>
                <Button variant="outline" onClick={handleLogout}>
                  Log ud
                </Button>
              </div>
            </div>
          </div>
        );
      }
      // På /admin/profil: tillad adgang men vis et banner i toppen så de kan se MFA-sektionen
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed bottom-6 right-6 z-50 rounded-xl bg-accent p-3.5 text-accent-foreground shadow-xl md:hidden"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <AdminSidebar
        user={user}
        profile={profile}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        unreadMessages={unreadMessages}
        urgentMessages={urgentMessages}
        recentMessages={recentMessages}
        recentChatMessages={recentChatMessages}
        dlqCount={dlqCount}
        carsWithoutImagesCount={carsWithoutImagesCount}
        verificationStaleCount={verificationStaleCount}
        verificationBadgeThresholdHours={verificationBadgeThresholdHours}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-muted p-6 md:p-10">
        <Outlet />
      </main>
    </div>
  );
}
